import { useCallback, useMemo } from "react";
import { navConfig, type NavItem } from "@/config/navConfig";
import { useUserStore } from "@/store/useUserStore";
import type { UserRole } from "@/types/userType";

/**
 * Role + permission filtered top-level navigation, derived from `navConfig`.
 *
 * Single source of truth shared by the desktop/drawer Sidebar and the mobile
 * BottomNav so the two can never drift apart. Sub-items are filtered the same
 * way the sidebar always has (role AND permission); super admins bypass the
 * permission gate.
 */
export const useFilteredNav = (): NavItem[] => {
  const role = useUserStore((s) => s.role);
  const user = useUserStore((s) => s.user);
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin);
  const isPartner = useUserStore((s) => s.isPartner);

  // "partner" is a central flag, not the single derived workspace role (a
  // partner's derived role is usually "admin"), so surface it explicitly here
  // — otherwise nav items gated on ["partner"] could never match a partner.
  const userRoles = useMemo(() => {
    const base = role ? [role as UserRole] : user?.roles || [];
    return isPartner && !base.includes('partner' as UserRole)
      ? [...base, 'partner' as UserRole]
      : base;
  }, [role, user, isPartner]);
  const userPermissions = useMemo<string[]>(
    () => (user as { permissions?: string[] } | null | undefined)?.permissions ?? [],
    [user],
  );

  const hasRoleAccess = useCallback(
    (roles?: UserRole[]) => {
      if (!roles || roles.length === 0) return true;
      return roles.some((r) => userRoles.includes(r));
    },
    [userRoles],
  );

  const hasPermissionAccess = useCallback(
    (requiredPermissions?: string[]) => {
      if (!requiredPermissions || requiredPermissions.length === 0) return true;
      if (isSuperAdmin) return true;
      return requiredPermissions.every((perm) => userPermissions.includes(perm));
    },
    [userPermissions, isSuperAdmin],
  );

  return useMemo(
    () =>
      navConfig
        .map((item) => ({
          ...item,
          items: item.items?.filter(
            (sub) =>
              !sub.hidden &&
              hasRoleAccess(sub.roles as UserRole[]) &&
              hasPermissionAccess(sub.requiredPermissions),
          ),
        }))
        .filter(
          (item) =>
            !item.hidden &&
            hasRoleAccess(item.roles as UserRole[]) &&
            hasPermissionAccess(item.requiredPermissions) &&
            // A grouping-only parent with no visible children is dropped entirely
            // (rather than rendering as a leaf link to its href).
            !(item.groupOnly && (item.items?.length ?? 0) === 0),
        ),
    [hasRoleAccess, hasPermissionAccess],
  );
};
